/**
 * RewardDetail Page
 * 
 * Displays complete information for a single reward catalog item.
 * Desktop: Clean sectioned layout with unified appearance
 * Mobile: Card-based layout for better scrolling
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { logger } from '@/app/utils/logger';
import { RewardCategoryBadge, RewardStatusBadge, RewardVariantBadge } from '@/components/common/Badge';
import { RewardUIContent } from '@/components/rewards/RewardUIContent';
import { RewardConfigDisplay } from '@/components/rewards/RewardConfigDisplay';
import { RewardResponse } from '@/app/types/rewards';
import { formatPointCost, formatDollarValue, getWorkflowTypeLabel, getRedemptionTypeLabel } from '@/app/types/rewards';
import { rewardService } from '@/app/services/rewardService';
import { auditService } from '@/app/services';
import { useAppDispatch } from '@/app/hooks/redux';
import { invalidate as invalidateRewardsList } from '@/app/store/rewardsSlice';

import './RewardDetail.scss';

export const RewardDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [reward, setReward] = useState<RewardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const rewardData = await rewardService.getRewardById(id);
        if (!rewardData) {
          setError('Reward not found');
          return;
        }
        
        setReward(rewardData);
        
        // Log record view for audit
        auditService.logRecordView('reward', id, {
          page_url: `/rewards/${id}`,
        }).catch((err) => logger.warn('Failed to log record view', { error: err }));
      } catch (err) {
        setError('Failed to load reward');
        logger.error('Failed to load reward', { error: err, rewardId: id });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleArchive = async () => {
    if (!id || !reward) return;
    
    if (!window.confirm(`Are you sure you want to archive "${reward.ux_reward_title}"?`)) {
      return;
    }
    
    setArchiving(true);
    try {
      const updated = await rewardService.archiveReward(id);
      if (updated) {
        // Log archive action for audit
        auditService.logRecordModification('updated', 'reward', id, {
          action: 'archived',
          previous_status: reward?.status,
          new_status: 'ARCHIVED',
        }).catch((err) => logger.warn('Failed to log archive action', { error: err }));
        
        setReward(updated);
        dispatch(invalidateRewardsList()); // Mark list cache as stale so it refetches on return
      }
    } catch (err) {
      logger.error('Failed to archive reward', { error: err, rewardId: id });
      setError('Failed to archive reward');
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async () => {
    if (!id || !reward) return;
    
    setRestoring(true);
    try {
      const updated = await rewardService.restoreReward(id);
      if (updated) {
        // Log restore action for audit
        auditService.logRecordModification('updated', 'reward', id, {
          action: 'restored',
          previous_status: 'ARCHIVED',
          new_status: updated.status,
        }).catch((err) => logger.warn('Failed to log restore action', { error: err }));
        
        setReward(updated);
        dispatch(invalidateRewardsList()); // Mark list cache as stale so it refetches on return
      }
    } catch (err) {
      logger.error('Failed to restore reward', { error: err, rewardId: id });
      setError('Failed to restore reward');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <div className="reward-detail-page">
        <div className="loading-state">
          <div className="spinner" />
          <span>Loading reward...</span>
        </div>
      </div>
    );
  }

  if (error || !reward) {
    return (
      <div className="reward-detail-page">
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error || 'Reward not found'}</span>
          <button onClick={() => navigate('/rewards')} className="btn-back">
            Back to Rewards
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reward-detail-page">
      {/* Back Navigation */}
      <div className="back-nav">
        <button onClick={() => navigate('/rewards')} className="btn-back-link">
          ← Back to Rewards Catalog
        </button>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="item-title">{reward.ux_reward_title}</div>
          {reward.ux_reward_subtitle && (
            <p className="header-subtitle">{reward.ux_reward_subtitle}</p>
          )}
          <div className="header-badges">
            <RewardStatusBadge status={reward.status} />
            <RewardCategoryBadge category={reward.category} />
            <RewardVariantBadge variant={reward.reward_variant} />
          </div>
        </div>
        <div className="header-actions">
          <button 
            className="btn-edit"
            onClick={() => navigate(`/rewards/${id}/edit`)}
          >
            ✏️ Edit
          </button>
          {reward.status === 'ARCHIVED' ? (
            <button 
              className="btn-restore"
              onClick={handleRestore}
              disabled={restoring}
            >
              {restoring ? 'Restoring...' : '↩️ Restore'}
            </button>
          ) : (
            <button 
              className="btn-archive"
              onClick={handleArchive}
              disabled={archiving}
            >
              {archiving ? 'Archiving...' : '📦 Archive'}
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="detail-container">
        {/* Basic Info Section */}
        <section className="detail-section">
          <h2 className="section-title">Basic Information</h2>
          <div className="info-grid two-col">
            <div className="info-group">
              <div className="info-row">
                <span className="info-label">Reward ID</span>
                <span className="info-value mono small">
                  {reward.reward_id}
                  <button className="copy-btn" onClick={() => copyToClipboard(reward.reward_id)}>📋</button>
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Reward Key</span>
                <span className="info-value mono">{reward.reward_key}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Internal Name</span>
                <span className="info-value">{reward.reward_name}</span>
              </div>
            </div>
            <div className="info-group">
              <div className="info-row">
                <span className="info-label">Category</span>
                <span className="info-value"><RewardCategoryBadge category={reward.category} /></span>
              </div>
              <div className="info-row">
                <span className="info-label">Variant</span>
                <span className="info-value"><RewardVariantBadge variant={reward.reward_variant} /></span>
              </div>
              <div className="info-row">
                <span className="info-label">Workflow Type</span>
                <span className="info-value">
                  {reward.ui_content_json?.workflow_type 
                    ? getWorkflowTypeLabel(reward.ui_content_json.workflow_type)
                    : '—'}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="detail-section">
          <h2 className="section-title">Pricing</h2>
          <div className="pricing-grid">
            <div className="pricing-item primary">
              <span className="pricing-label">Point Cost</span>
              <span className="pricing-value">{formatPointCost(reward.point_cost)}</span>
            </div>
            <div className="pricing-item">
              <span className="pricing-label">Dollar Value</span>
              <span className="pricing-value">{formatDollarValue(reward.dollar_value)}</span>
            </div>
            {reward.out_of_pocket_cost !== null && (
              <div className="pricing-item">
                <span className="pricing-label">Out of Pocket</span>
                <span className="pricing-value">{formatDollarValue(reward.out_of_pocket_cost)}</span>
              </div>
            )}
          </div>
        </section>

        {/* Reward Configuration Section */}
        <section className="detail-section">
          <h2 className="section-title">Reward Configuration</h2>
          <p className="section-subtitle">Limits, availability, pool membership, and completion points</p>
          <RewardConfigDisplay config={reward.reward_config} />
        </section>

        {/* Display Content Section */}
        <section className="detail-section">
          <h2 className="section-title">Display Content</h2>
          <div className="info-grid">
            <div className="info-row">
              <span className="info-label">Title</span>
              <span className="info-value">{reward.ux_reward_title}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Subtitle</span>
              <span className="info-value">{reward.ux_reward_subtitle || '—'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Description</span>
              <span className="info-value">{reward.ux_reward_description || '—'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Image URL</span>
              <span className="info-value mono small">{reward.ux_image_url || 'Not configured'}</span>
            </div>
          </div>
        </section>

        {/* UI Configuration Section */}
        <section className="detail-section">
          <h2 className="section-title">UI Configuration</h2>
          <p className="section-subtitle">Parsed content from ui_content_json</p>
          <RewardUIContent content={reward.ui_content_json} />
        </section>

        {/* Redemption Configuration Section */}
        <section className="detail-section">
          <h2 className="section-title">Redemption Configuration</h2>
          <p className="section-subtitle">How this reward is fulfilled</p>
          {reward.redemption_config ? (
            <div className="info-grid">
              <div className="info-row">
                <span className="info-label">Type</span>
                <span className="info-value">
                  <span className={`redemption-type-badge ${reward.redemption_config.type}`}>
                    {getRedemptionTypeLabel(reward.redemption_config.type)}
                  </span>
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Provider</span>
                <span className="info-value mono">{reward.redemption_config.provider}</span>
              </div>
              {reward.redemption_config.type === 'api' && reward.redemption_config.api && (
                <>
                  <div className="info-row">
                    <span className="info-label">API Endpoint</span>
                    <span className="info-value mono small">
                      {reward.redemption_config.api.method} {reward.redemption_config.api.endpoint}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Base URL (Prod)</span>
                    <span className="info-value mono small">
                      {reward.redemption_config.api.base_url?.prod || '—'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Base URL (Non-Prod)</span>
                    <span className="info-value mono small">
                      {reward.redemption_config.api.base_url?.nonprod || '—'}
                    </span>
                  </div>
                  {reward.redemption_config.api.retry && (
                    <div className="info-row">
                      <span className="info-label">Retry Config</span>
                      <span className="info-value mono small">
                        {reward.redemption_config.api.retry.max_attempts} attempts, 
                        backoff: [{reward.redemption_config.api.retry.backoff_ms?.join(', ')}] ms
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="empty-config">
              <span className="muted">No redemption configuration set</span>
            </div>
          )}
        </section>

        {/* Metadata Section */}
        <section className="detail-section">
          <h2 className="section-title">Metadata</h2>
          <div className="timeline-grid">
            <div className="timeline-item">
              <span className="timeline-label">Created By</span>
              <span className="timeline-value">{reward.created_by}</span>
            </div>
            <div className="timeline-item muted">
              <span className="timeline-label">Created At</span>
              <span className="timeline-value">{formatDate(reward.created_at)}</span>
            </div>
            <div className="timeline-item muted">
              <span className="timeline-label">Updated At</span>
              <span className="timeline-value">{formatDate(reward.updated_at)}</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default RewardDetail;
